import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { useParams, useNavigate } from 'react-router-dom'
import { API_URL } from "../../../config";
import axiosInstance from '../../../utils/axiosInstance'
import './UpdateService.css'

function UpdateService() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [serviceName, setServiceName] = useState('')
  const [servicePrice, setServicePrice] = useState('')
  const [deptId, setDeptId] = useState('')
  const [departments, setDepartments] = useState([])
  const [is_recurring, setIs_recurring] = useState(false)
  const [subscriber, setSubscriber] = useState('')
  const [plan, setPlan] = useState('')
  const [billingCycle, setBillingCycle] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('')
  const [createdDate, setCreatedDate] = useState(new Date().toISOString().split('T')[0])
  const [expiringOn, setExpiringOn] = useState('')
  const [billToAddress, setBillToAddress] = useState('')
  const [startDate, setStartDate] = useState('')
  const [duration, setDuration] = useState(1)
  const [durationUnit, setDurationUnit] = useState('months')
  const [renewalType, setRenewalType] = useState('monthly')
  const [reminderDaysBefore, setReminderDaysBefore] = useState(3)
  const [errors, setErrors] = useState({});

  // Fetch service by ID
  useEffect(() => {
    axios.get(`${API_URL}/api/getServiceById/${id}`)
      .then(res => {
        if (res.data) {
          setServiceName(res.data.serviceName)
          setServicePrice(res.data.servicePrice)
          setDeptId(res.data.deptId?._id || '')
          setIs_recurring(res.data.is_recurring || false)
          if (res.data.subscription) {
            setStartDate(res.data.subscription.start_date ? res.data.subscription.start_date.split('T')[0] : '')
            setDuration(res.data.subscription.duration_value || 1)
            setRenewalType(res.data.subscription.renewal_type || 'monthly')
            setDurationUnit(res.data.subscription.renewal_type === 'yearly' ? 'years' : 'months')
            setReminderDaysBefore(
              Number.isInteger(res.data.subscription.reminder_days_before)
                ? res.data.subscription.reminder_days_before
                : 3
            )
            setBillingCycle(res.data.subscription.billing_cycle || '')
          }
        }
      })
      .catch(err => console.error(err))
  }, [id])

  // Fetch departments for dropdown
  useEffect(() => {
    axios.get(`${API_URL}/api/getDepartment`)
      .then(res => setDepartments(res.data))
      .catch(err => console.error(err))
  }, [])

  // Update service
  const validate = () => {
    const newErrors = {};
    if (!serviceName.trim()) newErrors.serviceName = "Service name is required.";
    if (!servicePrice) newErrors.servicePrice = "Service price is required.";
    else if (isNaN(servicePrice) || parseFloat(servicePrice) <= 0) newErrors.servicePrice = "Service price must be a positive number.";
    if (!deptId) newErrors.deptId = "Department is required.";
    
    if (is_recurring) {
      if (!subscriber.trim()) newErrors.subscriber = "Subscriber is required.";
      if (!plan.trim()) newErrors.plan = "Plan name is required.";
      if (!billingCycle.trim()) newErrors.billingCycle = "Billing cycle is required.";
      if (!paymentMethod) newErrors.paymentMethod = "Payment method is required.";
      if (!createdDate) newErrors.createdDate = "Created date is required.";
      if (!expiringOn) newErrors.expiringOn = "Expiring date is required.";
      if (!startDate) newErrors.startDate = "Start date is required.";
      if (!duration || Number(duration) <= 0) newErrors.duration = "Duration must be a positive number.";
      if (!renewalType) newErrors.renewalType = "Renewal type is required.";
      if (Number(reminderDaysBefore) < 0) newErrors.reminderDaysBefore = "Reminder days cannot be negative.";
    }
    return newErrors;
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    try {
      await axiosInstance.put(`/api/UpdateService/${id}`, {
        serviceName,
        servicePrice,
        deptId,
        is_recurring,
        subscriber,
        plan,
        billingCycle,
        paymentMethod,
        createdDate,
        expiringOn,
        billToAddress,
        startDate,
        duration,
        durationUnit,
        renewalType,
        reminderDaysBefore
      });
      alert("Service updated successfully");
      navigate('/admin/service');
    } catch (err) {
      console.error(err);
      if (err.response && err.response.data && err.response.data.message) {
        alert(err.response.data.message);
      } else {
        alert("Error updating service");
      }
    }
  };

  return (
    <div className="update-service-page">
      <div className="update-service-shell">
        <div className="update-service-header">
          <p className="update-service-eyebrow">Service Management</p>
          <h3>Update Service</h3>
          <span>Modify service details while keeping department mapping intact</span>
        </div>

        <div className="update-service-card">
          <form onSubmit={handleUpdate}>
            <div className="update-service-grid">
              <div className="update-service-field">
                <label className="form-label fw-semibold">Service Name</label>
                <input
                  type="text"
                  className={`form-control update-service-input ${errors.serviceName ? 'is-invalid' : ''}`}
                  value={serviceName}
                  onChange={(e) => { setServiceName(e.target.value); setErrors(prev => ({ ...prev, serviceName: '' })); }}
                  required
                />
                {errors.serviceName && <div className="text-danger small mt-1">{errors.serviceName}</div>}
              </div>

              <div className="update-service-field">
                <label className="form-label fw-semibold">Service Price (INR)</label>
                <input
                  type="number"
                  className={`form-control update-service-input ${errors.servicePrice ? 'is-invalid' : ''}`}
                  value={servicePrice}
                  onChange={(e) => { setServicePrice(e.target.value); setErrors(prev => ({ ...prev, servicePrice: '' })); }}
                  required
                />
                {errors.servicePrice && <div className="text-danger small mt-1">{errors.servicePrice}</div>}
              </div>

              <div className="update-service-field update-service-field-full">
                <label className="form-label fw-semibold">Department</label>
                <select
                  className={`form-select update-service-input ${errors.deptId ? 'is-invalid' : ''}`}
                  value={deptId}
                  onChange={(e) => { setDeptId(e.target.value); setErrors(prev => ({ ...prev, deptId: '' })); }}
                  required
                >
                  <option value="">Select Department</option>
                  {departments.map(dept => (
                    <option key={dept._id} value={dept._id}>
                      {dept.deptName}
                    </option>
                  ))}
                </select>
                {errors.deptId && <div className="text-danger small mt-1">{errors.deptId}</div>}
              </div>

              {/* Recurring Service Checkbox */}
              <div className="update-service-field update-service-field-full mt-3">
                <div className="form-check d-flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    className="form-check-input update-service-checkbox"
                    id="is_recurring"
                    checked={is_recurring}
                    onChange={(e) => setIs_recurring(e.target.checked)}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <label className="form-check-label fw-semibold" htmlFor="is_recurring" style={{ cursor: 'pointer' }}>
                    Is Recurring Service?
                  </label>
                </div>
              </div>

              {is_recurring && (
                <div className="update-service-field update-service-field-full mt-2">
                  <div className="update-subscription-panel">
                    <h4 className="update-subscription-title">Add New Subscription</h4>
                    <div className="update-subscription-grid">
                    <div className="update-subscription-item">
                      <label className="update-subscription-label">SUBSCRIBER</label>
                      <input
                        type="text"
                        className={`form-control update-subscription-input ${errors.subscriber ? 'is-invalid' : ''}`}
                        value={subscriber}
                        onChange={(e) => { setSubscriber(e.target.value); setErrors(prev => ({ ...prev, subscriber: '' })); }}
                        placeholder="e.g. Client name"
                      />
                      {errors.subscriber && <div className="text-danger small mt-1">{errors.subscriber}</div>}
                    </div>
                    <div className="update-subscription-item">
                      <label className="update-subscription-label">PLAN NAME</label>
                      <input
                        type="text"
                        className={`form-control update-subscription-input ${errors.plan ? 'is-invalid' : ''}`}
                        value={plan}
                        onChange={(e) => { setPlan(e.target.value); setErrors(prev => ({ ...prev, plan: '' })); }}
                        placeholder="e.g. Enterprise"
                      />
                      {errors.plan && <div className="text-danger small mt-1">{errors.plan}</div>}
                    </div>
                    <div className="update-subscription-item">
                      <label className="update-subscription-label">BILLING CYCLE</label>
                      <input
                        type="text"
                        className={`form-control update-subscription-input ${errors.billingCycle ? 'is-invalid' : ''}`}
                        value={billingCycle}
                        onChange={(e) => { setBillingCycle(e.target.value); setErrors(prev => ({ ...prev, billingCycle: '' })); }}
                        placeholder="e.g. 30 days"
                      />
                      {errors.billingCycle && <div className="text-danger small mt-1">{errors.billingCycle}</div>}
                    </div>
                    <div className="update-subscription-item">
                      <label className="update-subscription-label">PAYMENT METHOD</label>
                      <select
                        className={`form-select update-subscription-input ${errors.paymentMethod ? 'is-invalid' : ''}`}
                        value={paymentMethod}
                        onChange={(e) => { setPaymentMethod(e.target.value); setErrors(prev => ({ ...prev, paymentMethod: '' })); }}
                      >
                        <option value="" disabled>Select Payment Method</option>
                        <option value="UPI">UPI</option>
                        <option value="Net Banking">Net Banking</option>
                        <option value="Debit Card">Debit Card</option>
                        <option value="Credit Card">Credit Card</option>
                        <option value="PayPal">PayPal</option>
                        <option value="Cash">Cash</option>
                      </select>
                      {errors.paymentMethod && <div className="text-danger small mt-1">{errors.paymentMethod}</div>}
                    </div>
                    <div className="update-subscription-item">
                      <label className="update-subscription-label">AMOUNT</label>
                      <input type="number" className="form-control update-subscription-input" value={servicePrice} readOnly />
                    </div>
                    <div className="update-subscription-item">
                      <label className="update-subscription-label">CREATED ON</label>
                      <input
                        type="date"
                        className={`form-control update-subscription-input ${errors.createdDate ? 'is-invalid' : ''}`}
                        value={createdDate}
                        onChange={(e) => { setCreatedDate(e.target.value); setErrors(prev => ({ ...prev, createdDate: '' })); }}
                      />
                      {errors.createdDate && <div className="text-danger small mt-1">{errors.createdDate}</div>}
                    </div>
                    <div className="update-subscription-item">
                      <label className="update-subscription-label">EXPIRING ON</label>
                      <input
                        type="date"
                        className={`form-control update-subscription-input ${errors.expiringOn ? 'is-invalid' : ''}`}
                        value={expiringOn}
                        onChange={(e) => { setExpiringOn(e.target.value); setErrors(prev => ({ ...prev, expiringOn: '' })); }}
                      />
                      {errors.expiringOn && <div className="text-danger small mt-1">{errors.expiringOn}</div>}
                    </div>
                    <div className="update-subscription-item">
                      <label className="update-subscription-label">BILL TO ADDRESS</label>
                      <input
                        type="text"
                        className="form-control update-subscription-input"
                        value={billToAddress}
                        onChange={(e) => setBillToAddress(e.target.value)}
                        placeholder="Optional"
                      />
                    </div>
                    <div className="update-subscription-item">
                      <label className="update-subscription-label">SERVICE NAME (FOR SUBSCRIPTION)</label>
                      <input type="text" className="form-control update-subscription-input" value={serviceName} readOnly />
                    </div>
                    <div className="update-subscription-item">
                      <label className="update-subscription-label">AMOUNT (FOR SUBSCRIPTION)</label>
                      <input type="number" className="form-control update-subscription-input" value={servicePrice} readOnly />
                    </div>
                    <div className="update-subscription-item">
                      <label className="update-subscription-label">START DATE</label>
                      <input
                        type="date"
                        className={`form-control update-subscription-input ${errors.startDate ? 'is-invalid' : ''}`}
                        value={startDate}
                        onChange={(e) => { setStartDate(e.target.value); setErrors(prev => ({ ...prev, startDate: '' })); }}
                      />
                      {errors.startDate && <div className="text-danger small mt-1">{errors.startDate}</div>}
                    </div>
                    <div className="update-subscription-item">
                      <label className="update-subscription-label">DURATION</label>
                      <div className="update-subscription-duration">
                        <input
                          type="number"
                          min="1"
                          step="1"
                          className={`form-control update-subscription-input ${errors.duration ? 'is-invalid' : ''}`}
                          value={duration}
                          onChange={(e) => { setDuration(e.target.value); setErrors(prev => ({ ...prev, duration: '' })); }}
                        />
                        <select
                          className="form-select update-subscription-input"
                          value={durationUnit}
                          onChange={(e) => {
                            setDurationUnit(e.target.value);
                            setRenewalType(e.target.value === 'years' ? 'yearly' : 'monthly');
                            setErrors(prev => ({ ...prev, durationUnit: '', renewalType: '' }));
                          }}
                        >
                          <option value="months">Months</option>
                          <option value="years">Years</option>
                        </select>
                      </div>
                      {errors.duration && <div className="text-danger small mt-1">{errors.duration}</div>}
                    </div>
                    <div className="update-subscription-item">
                      <label className="update-subscription-label">RENEWAL TYPE</label>
                      <select
                        className={`form-select update-subscription-input ${errors.renewalType ? 'is-invalid' : ''}`}
                        value={renewalType}
                        onChange={(e) => { setRenewalType(e.target.value); setErrors(prev => ({ ...prev, renewalType: '' })); }}
                      >
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                      {errors.renewalType && <div className="text-danger small mt-1">{errors.renewalType}</div>}
                    </div>
                  </div>
                  </div>
                </div>
              )}
            </div>

            <div className="update-service-actions">
              <button
                type="button"
                className="btn update-service-btn update-service-btn-muted"
                onClick={() => navigate('/admin/service')}
              >
                Back
              </button>
              <button
                type="submit"
                className="btn update-service-btn update-service-btn-primary"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default UpdateService
